import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import Home from "@/pages/Home";
import Gallery from "@/pages/Gallery";
import FFL from "@/pages/FFL";
import Shop from "@/pages/Shop";
import Admin from "@/pages/Admin";
import EstimateView from "@/pages/EstimateView";
import InvoiceView from "@/pages/InvoiceView";
import LoadingScreen from "@/components/LoadingScreen";
import BulletHoleEffect from "@/components/BulletHoleEffect";
import SiteBanner from "@/components/SiteBanner";
import CookieConsent from "@/components/CookieConsent";
import PublicRouteGuard from "@/components/PublicRouteGuard";
import GhostFlagBackground from "@/components/GhostFlagBackground";
import HexagonBackground from "@/components/HexagonBackground";
import CursorTrail from "@/components/CursorTrail";
import SiteCursorStyle from "@/components/SiteCursorStyle";
import MaintenanceGate from "@/components/MaintenanceGate";
import DesignStyleInjector from "@/components/DesignStyleInjector";
import { SiteSettingsProvider, useSiteSettings } from "@/contexts/SiteSettingsContext";
import { useAnalytics } from "@/lib/useAnalytics";

const toastOptions = {
  style: {
    background: '#1e293b',
    color: '#fff',
    border: '1px solid #475569'
  }
};

function SiteBackground() {
  const { theme } = useSiteSettings();
  const bg = theme?.background || { variant: 'ghost_flag', intensity: 'subtle' };
  switch (bg.variant) {
    case 'standard_navy':
      return null;  // Base navy body color already applied globally
    case 'ghost_flag':
      return <GhostFlagBackground intensity={bg.intensity} theme={bg.flag_theme || 'classic'} />;
    case 'hexagon':
      return <HexagonBackground intensity={bg.intensity} />;
    case 'custom_image':
      if (!bg.custom_image_url) return <GhostFlagBackground intensity="subtle" theme="classic" />;
      return (
        <div
          className="fixed inset-0 pointer-events-none -z-10"
          data-testid="custom-image-bg"
          style={{
            backgroundImage: `url("${bg.custom_image_url}")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.22,
          }}
        />
      );
    default:
      return <GhostFlagBackground intensity="subtle" theme="classic" />;
  }
}

function SiteCursor() {
  const { theme } = useSiteSettings();
  const cursor = theme?.cursor || {};
  const enabled = cursor.trail_enabled !== false;
  if (!enabled) return null;
  return (
    <CursorTrail
      shape={cursor.trail_shape || 'hexagon'}
      color={cursor.trail_color || 'silver'}
      size={cursor.trail_size || 'medium'}
      intensity={cursor.trail_intensity || cursor.trail_style || 'subtle'}
    />
  );
}

function SitePointer() {
  const { theme } = useSiteSettings();
  const cursor = theme?.cursor || {};
  return <SiteCursorStyle styleKey={cursor.cursor_style || 'default'} />;
}

function AppContent() {
  const location = useLocation();
  const isAdminLike = location.pathname === "/admin" || location.pathname.startsWith("/estimate/") || location.pathname.startsWith("/invoice/");
  const showDecorations = !isAdminLike;
  const { maintenance } = useSiteSettings();
  const previewMode = new URLSearchParams(location.search).get('preview') === '1';
  const showMaintenance = !isAdminLike && !previewMode && maintenance?.enabled;

  useAnalytics();

  // Full-screen maintenance mode takes over the public site (never /admin,
  // never estimate/invoice public URLs, and admins can bypass with ?preview=1).
  if (showMaintenance) {
    return (
      <>
        <MaintenanceGate />
        <Toaster position="top-right" theme="dark" className="z-[9999]" toastOptions={toastOptions} />
      </>
    );
  }

  return (
    <>
      <DesignStyleInjector />
      {/* Background is now settings-driven (Ghost Flag by default). Removed the
          bright red/white/blue American flag per the brief — the Ghost Flag
          option renders the same design in muted, tone-on-tone colors. */}
      {showDecorations && <SiteBackground />}
      {showDecorations && <BulletHoleEffect />}
      {showDecorations && <SiteCursor />}
      {showDecorations && <SitePointer />}
      {showDecorations && <SiteBanner />}
      {showDecorations && <CookieConsent />}
      <Routes>
        <Route path="/" element={<PublicRouteGuard pageKey="home"><Home /></PublicRouteGuard>} />
        <Route path="/gallery" element={<PublicRouteGuard pageKey="gallery"><Gallery /></PublicRouteGuard>} />
        <Route path="/ffl-transfers" element={<PublicRouteGuard pageKey="ffl"><FFL /></PublicRouteGuard>} />
        <Route path="/shop" element={<PublicRouteGuard pageKey="shop"><Shop /></PublicRouteGuard>} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/estimate/:id" element={<EstimateView />} />
        <Route path="/invoice/:id" element={<InvoiceView />} />
      </Routes>
      <Toaster
        position="top-right"
        theme="dark"
        className="z-[9999]"
        toastOptions={toastOptions}
      />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <LoadingScreen />
      <BrowserRouter>
        <SiteSettingsProvider>
          <AppContent />
        </SiteSettingsProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
